import { useState } from 'react';
import { Link } from 'react-router-dom';
import { tokenAnalyzerAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FaChartLine, FaLightbulb, FaRocket, FaInfoCircle, FaLock } from 'react-icons/fa';

const TokenAnalyzer = () => {
  const { currentUser, userProfile } = useAuth();
  const [promptText, setPromptText] = useState('');
  const [selectedModel, setSelectedModel] = useState('gpt-4o');
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [optimization, setOptimization] = useState(null);

  const models = [
    // OpenAI
    { value: 'gpt-4o', label: 'GPT-4o', description: 'Omni, strong all-rounder' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini', description: 'Cost-efficient' },
    { value: 'gpt-4.1', label: 'GPT-4.1', description: 'Reasoning-optimized' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini', description: 'Lightweight' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo', description: 'Fast GPT-4' },
    { value: 'gpt-4', label: 'GPT-4', description: 'Legacy' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo', description: 'Legacy fast' },
    { value: 'o4-mini', label: 'o4-mini', description: 'OpenAI o-series' },
    { value: 'o3', label: 'o3', description: 'OpenAI o-series' },
    // Anthropic
    { value: 'claude-3.5-sonnet', label: 'Claude 3.5 Sonnet', description: 'Balanced' },
    { value: 'claude-3.5-haiku', label: 'Claude 3.5 Haiku', description: 'Efficient' },
    { value: 'claude-3-opus', label: 'Claude 3 Opus', description: 'Strong reasoning' },
    { value: 'claude-3-sonnet', label: 'Claude 3 Sonnet', description: 'Balanced' },
    { value: 'claude-3-haiku', label: 'Claude 3 Haiku', description: 'Fast' },
    // Google
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro', description: 'General' },
    { value: 'gemini-1.5-pro-latest', label: 'Gemini 1.5 Pro (latest)', description: 'Latest' },
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash', description: 'Fast' },
    { value: 'gemini-1.5-flash-latest', label: 'Gemini 1.5 Flash (latest)', description: 'Latest fast' },
    // Meta
    { value: 'llama-2', label: 'Llama 2', description: 'Open source' },
    { value: 'llama-3', label: 'Llama 3', description: 'Open source' },
    { value: 'llama-3.1-8b-instruct', label: 'Llama 3.1 8B Instruct', description: 'Open source' },
    { value: 'llama-3.1-70b-instruct', label: 'Llama 3.1 70B Instruct', description: 'Open source' },
    // Mistral
    { value: 'mistral', label: 'Mistral', description: 'Efficient' },
    { value: 'mistral-large-latest', label: 'Mistral Large (latest)', description: 'Strong OSS' },
    { value: 'mixtral-8x7b-instruct', label: 'Mixtral 8x7B Instruct', description: 'MoE' },
    { value: 'mixtral-8x22b-instruct', label: 'Mixtral 8x22B Instruct', description: 'MoE' },
    // Cohere
    { value: 'command-r', label: 'Command R', description: 'Cohere' },
    { value: 'command-r-plus', label: 'Command R+', description: 'Cohere' },
    // DeepSeek
    { value: 'deepseek-chat', label: 'DeepSeek Chat', description: 'DeepSeek' },
    { value: 'deepseek-reasoner', label: 'DeepSeek Reasoner', description: 'Reasoning' },
    // Other
    { value: 'palm-2', label: 'PaLM 2', description: 'Legacy' },
  ];

  const handleAnalyze = async () => {
    if (!promptText.trim()) {
      toast.error('Please enter some text to analyze');
      return;
    }

    setAnalyzing(true);
    try {
      // Get token count and analysis
      const analyzeResponse = await tokenAnalyzerAPI.analyze(promptText, selectedModel);
      setResults(analyzeResponse.data);

      // If user is premium, get optimization suggestions
      if (userProfile?.is_premium) {
        try {
          const optimizeResponse = await tokenAnalyzerAPI.optimize(promptText, selectedModel);
          setOptimization(optimizeResponse.data);
        } catch (error) {
          console.log('Optimization not available');
        }
      }

      toast.success('Analysis complete!');
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Failed to analyze text');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCompare = async () => {
    if (!promptText.trim()) {
      toast.error('Please enter some text to compare');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await tokenAnalyzerAPI.compare(promptText);
      toast.success('Comparison complete!');
      // Display comparison results
      const comparisonData = response.data.comparison;
      console.log('Comparison:', comparisonData);

      // Update results to show comparison
      setResults({
        ...results,
        comparison: comparisonData,
      });
    } catch (error) {
      console.error('Comparison error:', error);
      toast.error('Failed to compare models');
    } finally {
      setAnalyzing(false);
    }
  };

  const getEfficiencyColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getEfficiencyLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Poor';
  };

  // Require authentication
  if (!currentUser) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-white rounded-lg shadow-xl p-12 text-center">
          <FaLock className="text-6xl text-boop-primary mx-auto mb-6" />
          <h1 className="text-3xl font-bold mb-4">Token Analyzer</h1>
          <p className="text-gray-600 text-lg mb-8">
            Sign in to access the Token Analyzer and optimize your AI prompts for efficiency
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-8 max-w-md mx-auto">
            <h3 className="font-semibold text-lg mb-3">What You'll Get:</h3>
            <ul className="text-left space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-boop-primary mr-2">✓</span>
                Accurate token counts for any AI model
              </li>
              <li className="flex items-start">
                <span className="text-boop-primary mr-2">✓</span>
                Efficiency scoring and optimization tips
              </li>
              <li className="flex items-start">
                <span className="text-boop-primary mr-2">✓</span>
                Cross-model comparisons
              </li>
              <li className="flex items-start">
                <span className="text-boop-primary mr-2">✓</span>
                Cost estimates for API usage
              </li>
            </ul>
          </div>

          <div className="flex gap-4 justify-center">
            <Link
              to="/login"
              className="bg-boop-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-boop-secondary transition"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="border-2 border-boop-primary text-boop-primary px-8 py-3 rounded-lg font-semibold hover:bg-boop-light transition"
            >
              Create Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Token Analyzer</h1>
        <p className="text-gray-600">
          Analyze your prompts to understand token usage and optimize for efficiency
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Input Section */}
        <div className="lg:col-span-2">
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-semibold mb-4">Enter Your Prompt</h2>

            <textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="Paste your AI prompt here to analyze token usage..."
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary mb-4"
              rows={10}
            />

            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">
                Target AI Model
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
              >
                {models.map((model) => (
                  <option key={model.value} value={model.value}>
                    {model.label} - {model.description}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleAnalyze}
                disabled={analyzing || !promptText.trim()}
                className="flex-1 flex items-center justify-center px-6 py-3 bg-boop-primary text-white rounded-lg hover:bg-boop-secondary transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChartLine className="mr-2" />
                {analyzing ? 'Analyzing...' : 'Analyze'}
              </button>
              <button
                onClick={handleCompare}
                disabled={analyzing || !promptText.trim()}
                className="flex-1 flex items-center justify-center px-6 py-3 border border-boop-primary text-boop-primary rounded-lg hover:bg-boop-light transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaRocket className="mr-2" />
                Compare Models
              </button>
            </div>
          </div>

          {/* Results Section */}
          {results && (
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <h2 className="text-xl font-semibold mb-4">Analysis Results</h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-boop-primary mb-1">
                    {results.tokenCount}
                  </div>
                  <div className="text-sm text-gray-600">Tokens</div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    {results.wordCount}
                  </div>
                  <div className="text-sm text-gray-600">Words</div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-1">
                    {results.charCount}
                  </div>
                  <div className="text-sm text-gray-600">Characters</div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <div className={`text-3xl font-bold mb-1 ${getEfficiencyColor(results.efficiencyScore)}`}>
                    {results.efficiencyScore}
                  </div>
                  <div className="text-sm text-gray-600">Efficiency</div>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-semibold">Token Efficiency</span>
                  <span className={`text-sm font-semibold ${getEfficiencyColor(results.efficiencyScore)}`}>
                    {getEfficiencyLabel(results.efficiencyScore)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full ${
                      results.efficiencyScore >= 80
                        ? 'bg-green-600'
                        : results.efficiencyScore >= 60
                        ? 'bg-yellow-600'
                        : 'bg-red-600'
                    }`}
                    style={{ width: `${results.efficiencyScore}%` }}
                  />
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <div className="flex">
                  <FaInfoCircle className="text-blue-500 mt-1 mr-3" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Tokens/Word Ratio: {(parseFloat(results.tokensPerWord) || 0).toFixed(2)}</p>
                    <p>
                      Your prompt uses an average of {(parseFloat(results.tokensPerWord) || 0).toFixed(2)} tokens per word.
                      Lower ratios indicate more efficient prompts.
                    </p>
                  </div>
                </div>
              </div>

              {/* Model Comparison */}
              {results.comparison && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-3">Model Comparison</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(results.comparison).map(([model, count]) => (
                      <div key={model} className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">{model}</div>
                        <div className="text-xl font-bold text-boop-primary">{count} tokens</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Optimization Suggestions (Premium) */}
          {optimization && userProfile?.is_premium && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-lg shadow-md border-2 border-yellow-300">
              <div className="flex items-center mb-4">
                <FaLightbulb className="text-yellow-600 text-2xl mr-3" />
                <h2 className="text-xl font-semibold">Optimization Suggestions</h2>
                <span className="ml-auto px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">
                  PREMIUM
                </span>
              </div>

              <div className="space-y-3">
                {optimization.suggestions?.map((suggestion, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg">
                    <div className="font-semibold text-gray-800 mb-2">{suggestion.title}</div>
                    <p className="text-sm text-gray-600 mb-2">{suggestion.description}</p>
                    {suggestion.example && (
                      <div className="bg-gray-50 p-2 rounded text-sm font-mono">
                        {suggestion.example}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {optimization.optimizedPrompt && (
                <div className="mt-4">
                  <h3 className="font-semibold mb-2">Optimized Version:</h3>
                  <div className="bg-white p-4 rounded-lg border-2 border-green-300">
                    <pre className="whitespace-pre-wrap text-sm">{optimization.optimizedPrompt}</pre>
                  </div>
                  <div className="mt-2 text-sm text-green-700 font-semibold">
                    Estimated savings: {optimization.tokenSavings} tokens ({optimization.percentageSaved}%)
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Premium Upsell */}
          {results && !userProfile?.is_premium && (
            <div className="bg-gradient-to-r from-boop-primary to-boop-secondary text-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-3">
                <FaLightbulb className="text-3xl mr-3" />
                <h3 className="text-xl font-semibold">Unlock AI-Powered Optimization</h3>
              </div>
              <p className="mb-4">
                Upgrade to Premium to get personalized suggestions on how to reduce token usage
                while maintaining prompt quality.
              </p>
              <button className="bg-white text-boop-primary px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition">
                Upgrade to Premium
              </button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h3 className="text-lg font-semibold mb-4">About Token Analysis</h3>
            <div className="text-sm text-gray-600 space-y-3">
              <p>
                Tokens are the basic units that AI models use to process text. Understanding
                token usage helps you optimize prompts and reduce API costs.
              </p>
              <div className="border-t pt-3">
                <h4 className="font-semibold text-gray-800 mb-2">Tips for Efficiency:</h4>
                <ul className="list-disc list-inside space-y-1">
                  <li>Use clear, concise language</li>
                  <li>Avoid repetition</li>
                  <li>Remove unnecessary words</li>
                  <li>Use bullet points for lists</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-boop-light p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-4 text-boop-primary">Token Pricing Guide</h3>
            <div className="text-sm text-gray-700 space-y-2">
              <div className="flex justify-between">
                <span>GPT-4:</span>
                <span className="font-semibold">$0.03/1K tokens</span>
              </div>
              <div className="flex justify-between">
                <span>GPT-3.5:</span>
                <span className="font-semibold">$0.002/1K tokens</span>
              </div>
              <div className="flex justify-between">
                <span>Claude 3 Opus:</span>
                <span className="font-semibold">$0.015/1K tokens</span>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                * Prices are approximate and may vary. Check official pricing for exact rates.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenAnalyzer;
